import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { toast } from "sonner";
import {
  HardHat, Plus, Pencil, Trash2, X, Save, Loader2, ArrowLeft, RefreshCw, Search,
  FileText, Download, Upload, Calendar, DollarSign, Users, ShoppingCart, Receipt,
  Wrench, ClipboardList, Settings, CheckCircle2, Clock, MapPin, User, Layers, ListChecks,
} from "lucide-react";
import { ClienteSearchSelect } from "../components/FacturaFormModal";
import { PresupuestoTab, CronogramaTab, ComprasGastosTab, EquiposTab, PersonalTab, FacturacionTab, ReportesTab } from "./ConstructoraSecciones";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BTN_PRI = "flex items-center gap-2 px-4 py-2 text-sm bg-arandu-blue hover:bg-blue-600 text-white rounded-lg transition-all font-body disabled:opacity-50";
const BTN_SEC = "flex items-center gap-2 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg transition-all border border-white/10";
const INPUT = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-arandu-blue/60";
const LABEL = "block text-xs text-slate-400 mb-1";

const ESTADOS = {
  en_estudio: { label: "En estudio", cls: "text-amber-400 bg-amber-400/10 border-amber-400/20" },
  contratado: { label: "Contratado", cls: "text-blue-400 bg-blue-400/10 border-blue-400/20" },
  en_ejecucion: { label: "En ejecución", cls: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20" },
  pausado: { label: "Pausado", cls: "text-orange-400 bg-orange-400/10 border-orange-400/20" },
  finalizado: { label: "Finalizado", cls: "text-violet-400 bg-violet-400/10 border-violet-400/20" },
  cancelado: { label: "Cancelado", cls: "text-slate-400 bg-slate-400/10 border-slate-400/20" },
};

const fmtNum = (n, moneda = "PYG") => {
  const v = Number(n) || 0;
  if (moneda === "PYG") return `₲ ${Math.round(v).toLocaleString("es-PY")}`;
  return `${moneda} ${v.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const errMsg = async (res, fallback) => {
  const e = await res.json().catch(() => ({}));
  return typeof e.detail === "string" ? e.detail : (Array.isArray(e.detail) ? e.detail.map(d => d.msg).join(" · ") : fallback);
};

const EstadoBadge = ({ estado }) => {
  const e = ESTADOS[estado] || { label: estado, cls: "text-slate-400 bg-slate-400/10 border-slate-400/20" };
  return <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${e.cls}`}>{e.label}</span>;
};

// Torta simple: cobrado vs pendiente
const Dona = ({ pct }) => {
  const r = 52, c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <svg viewBox="0 0 140 140" className="w-36 h-36">
      <circle cx="70" cy="70" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="16" />
      <circle cx="70" cy="70" r={r} fill="none" stroke="#10b981" strokeWidth="16" strokeLinecap="round"
        strokeDasharray={`${(p / 100) * c} ${c}`} transform="rotate(-90 70 70)" />
      <text x="70" y="68" textAnchor="middle" className="fill-white" style={{ fontSize: 24, fontWeight: 700 }}>{p}%</text>
      <text x="70" y="88" textAnchor="middle" className="fill-slate-400" style={{ fontSize: 10 }}>cobrado</text>
    </svg>
  );
};

export default function ConstructoraPage() {
  const navigate = useNavigate();
  const { token, hasPermission, activeEmpresaPropia, user } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${token}` };
  const slug = activeEmpresaPropia?.slug || "";
  const logoQs = slug ? `?logo_tipo=${encodeURIComponent(slug)}` : "";
  const logoQsAnd = slug ? `&logo_tipo=${encodeURIComponent(slug)}` : "";

  const isAdmin = ["admin", "super_admin", "gerente"].includes(user?.role);
  const isSuper = user?.role === "super_admin";
  const can = (p) => isAdmin || hasPermission(p);
  const canVer = can("constructora_proyectos.ver");
  const canCrear = can("constructora_proyectos.crear");
  const canEditar = can("constructora_proyectos.editar");
  const canEliminar = can("constructora_proyectos.eliminar");
  const canEstado = can("constructora_proyectos.cambiar_estado");
  const canContratoVer = can("constructora_proyectos.contrato_ver");
  const canContratoCargar = can("constructora_proyectos.contrato_cargar");
  const canPlan = can("constructora_proyectos.plan_cobros");
  const canConfig = can("constructora_configuracion.ver");
  const canGrupos = can("constructora_configuracion.gestionar_grupos");
  const canUnidades = can("constructora_configuracion.gestionar_unidades");

  const [vista, setVista] = useState("proyectos"); // proyectos | config
  const [proyectos, setProyectos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [abierto, setAbierto] = useState(null); // proyecto abierto
  const [modal, setModal] = useState(null); // null | {} | proyecto
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const emptyForm = () => ({
    nombre: "", cliente: null, ubicacion: "", tipo_obra: "", descripcion: "",
    fecha_inicio: new Date().toISOString().slice(0, 10), fecha_fin_prevista: "",
    monto_contrato: "", moneda: "PYG", responsable: "", observaciones: "",
  });

  const fetchAll = useCallback(async () => {
    if (!canVer) { setLoading(false); return; }
    try {
      const [rP, rC] = await Promise.all([
        fetch(`${API}/admin/constructora/proyectos${logoQs}`, { headers }),
        fetch(`${API}/admin/entidades?es_cliente=true${logoQsAnd}`, { headers }),
      ]);
      if (rP.ok) {
        const lista = await rP.json();
        setProyectos(lista);
        setAbierto(a => a ? (lista.find(x => x.id === a.id) || null) : a);
      }
      if (rC.ok) setClientes(await rC.json());
    } catch { toast.error("Error al cargar Constructora"); }
    setLoading(false);
    setRefreshing(false);
  }, [token, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return proyectos.filter(p =>
      (!filtroEstado || p.estado === filtroEstado) &&
      (!q || [p.nombre, p.numero, p.cliente_nombre, p.cliente_ruc, p.ubicacion].join(" ").toLowerCase().includes(q))
    );
  }, [proyectos, filtroEstado, busqueda]);

  const openModal = (p) => {
    if (p) {
      const cli = p.cliente_id ? (clientes.find(c => c.id === p.cliente_id) || { id: p.cliente_id, nombre: p.cliente_nombre, razon_social: p.cliente_razon_social, ruc: p.cliente_ruc }) : null;
      setForm({ ...emptyForm(), ...p, cliente: cli, monto_contrato: p.monto_contrato || "" });
    } else setForm(emptyForm());
    setModal(p || {});
  };

  const guardar = async () => {
    if (!form.nombre?.trim()) { toast.error("El nombre del proyecto es obligatorio"); return; }
    setSaving(true);
    try {
      const esEdicion = !!modal?.id;
      const url = esEdicion ? `${API}/admin/constructora/proyectos/${modal.id}` : `${API}/admin/constructora/proyectos${logoQs}`;
      const payload = {
        nombre: form.nombre, cliente_id: form.cliente?.id || null, ubicacion: form.ubicacion, tipo_obra: form.tipo_obra,
        descripcion: form.descripcion, fecha_inicio: form.fecha_inicio || null, fecha_fin_prevista: form.fecha_fin_prevista || null,
        monto_contrato: Number(form.monto_contrato) || 0, moneda: form.moneda, responsable: form.responsable, observaciones: form.observaciones,
      };
      const res = await fetch(url, { method: esEdicion ? "PUT" : "POST", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(await errMsg(res, "Error al guardar"));
      const saved = await res.json();
      toast.success(esEdicion ? "Proyecto actualizado" : "Proyecto creado");
      setModal(null);
      await fetchAll();
      if (!esEdicion) setAbierto(saved);
    } catch (e) { toast.error(e.message || "Error al guardar"); }
    setSaving(false);
  };

  const eliminar = async (p) => {
    if (!window.confirm(`¿Eliminar el proyecto ${p.numero} — ${p.nombre}?`)) return;
    try {
      const res = await fetch(`${API}/admin/constructora/proyectos/${p.id}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error(await errMsg(res, "Error al eliminar"));
      toast.success("Proyecto eliminado");
      setAbierto(null);
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  if (!canVer) {
    return (
      <div className="min-h-screen bg-arandu-dark text-white flex items-center justify-center p-6">
        <div className="text-center"><HardHat className="w-10 h-10 text-slate-600 mx-auto mb-3" /><p className="text-slate-400">No tenés permiso para ver Constructora.</p></div>
      </div>
    );
  }
  if (loading) {
    return <div className="min-h-screen bg-arandu-dark text-white flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-arandu-blue" /></div>;
  }

  return (
    <div className="min-h-screen bg-arandu-dark text-white">
      <div className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => (abierto ? setAbierto(null) : navigate(-1))} className="text-slate-400 hover:text-white p-1 -ml-1 rounded-lg hover:bg-white/5 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <HardHat className="w-6 h-6 text-yellow-400" />
            <div>
              <h1 className="text-lg font-bold text-white">{abierto ? `${abierto.numero} — ${abierto.nombre}` : "Constructora"}</h1>
              <p className="text-xs text-slate-400">{activeEmpresaPropia?.nombre || ""}{abierto ? ` — ${abierto.cliente_nombre || "Sin cliente"}` : " — Proyectos de obra"}</p>
            </div>
          </div>
          <button onClick={() => { setRefreshing(true); fetchAll(); }} disabled={refreshing} className={BTN_SEC}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {!abierto && (
        <div className="flex gap-1 px-6 pt-4 border-b border-white/10">
          {[{ k: "proyectos", l: "Proyectos", i: <HardHat className="w-4 h-4" />, show: true },
            { k: "config", l: "Configuración", i: <Settings className="w-4 h-4" />, show: canConfig }]
            .filter(t => t.show).map(t => (
              <button key={t.k} onClick={() => setVista(t.k)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${vista === t.k ? "text-white border-blue-500 bg-blue-500/10" : "text-slate-400 border-transparent hover:text-white hover:border-white/20"}`}>
                {t.i} {t.l}
              </button>
            ))}
        </div>
      )}

      <div className="p-6">
        {!abierto && vista === "proyectos" && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input className={`${INPUT} pl-9 w-64`} placeholder="Buscar proyecto, cliente, RUC…" value={busqueda} onChange={e => setBusqueda(e.target.value)} />
                </div>
                <select className={`${INPUT} w-44`} value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
                  <option value="">Todos los estados</option>
                  {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              {canCrear && <button onClick={() => openModal(null)} className={BTN_PRI}><Plus className="w-4 h-4" /> Nuevo proyecto</button>}
            </div>
            {filtrados.length === 0 ? (
              <div className="text-center text-slate-500 py-16 border border-dashed border-white/10 rounded-xl">
                <HardHat className="w-10 h-10 mx-auto mb-2 text-slate-600" />
                {proyectos.length === 0 ? "Todavía no hay proyectos. Creá el primero." : "Ningún proyecto coincide con el filtro."}
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {filtrados.map(p => (
                  <button key={p.id} onClick={() => setAbierto(p)} className="text-left bg-white/5 hover:bg-white/[0.08] border border-white/10 rounded-xl p-4 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-[11px] text-slate-500 font-mono">{p.numero}</div>
                        <div className="text-white font-semibold truncate">{p.nombre}</div>
                      </div>
                      <EstadoBadge estado={p.estado} />
                    </div>
                    <div className="mt-2 space-y-1 text-xs text-slate-400">
                      <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{p.cliente_nombre || "Sin cliente"}</div>
                      {p.ubicacion && <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{p.ubicacion}</div>}
                      <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{p.fecha_inicio || "—"} → {p.fecha_fin_prevista || "—"}</div>
                    </div>
                    <div className="mt-3 text-sm text-emerald-400 font-medium">{fmtNum(p.monto_contrato, p.moneda)}</div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {!abierto && vista === "config" && canConfig && (
          <ConfigSecciones
            headers={headers} logoQs={logoQs} canGrupos={canGrupos} canUnidades={canUnidades} isSuper={isSuper}
          />
        )}

        {abierto && (
          <ProyectoDetalle
            proyecto={abierto} headers={headers} clientes={clientes}
            perms={{ canEditar, canEliminar, canEstado, canContratoVer, canContratoCargar, canPlan }}
            onEditar={() => openModal(abierto)} onEliminar={() => eliminar(abierto)} onChanged={fetchAll}
          />
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">{modal?.id ? "Editar proyecto" : "Nuevo proyecto"}</h2>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className={LABEL}>Nombre del proyecto *</label>
                <input className={INPUT} value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Construcción de 2ª planta de vivienda" />
              </div>
              <div>
                <label className={LABEL}>Cliente (dueño del proyecto) — si no existe, se crea en el Catastro desde acá</label>
                <ClienteSearchSelect
                  empresas={clientes} value={form.cliente} token={token} logo={slug}
                  onSelect={c => setForm({ ...form, cliente: c })}
                  onCreated={nuevo => setClientes(l => [nuevo, ...l])}
                />
                {form.cliente?.id && (
                  <p className="text-[11px] text-slate-500 mt-1">Razón social, RUC, teléfono y dirección se toman del Catastro y quedan guardados en el proyecto.</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={LABEL}>Ubicación de la obra</label><input className={INPUT} value={form.ubicacion || ""} onChange={e => setForm({ ...form, ubicacion: e.target.value })} /></div>
                <div><label className={LABEL}>Tipo de obra</label><input className={INPUT} value={form.tipo_obra || ""} onChange={e => setForm({ ...form, tipo_obra: e.target.value })} placeholder="Vivienda, edificio, galpón…" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={LABEL}>Fecha de comienzo</label><DateInput className={INPUT} value={form.fecha_inicio || ""} onChange={e => setForm({ ...form, fecha_inicio: e.target.value })} /></div>
                <div><label className={LABEL}>Fin previsto</label><DateInput className={INPUT} value={form.fecha_fin_prevista || ""} onChange={e => setForm({ ...form, fecha_fin_prevista: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><label className={LABEL}>Monto del contrato</label><input type="number" min="0" className={INPUT} value={form.monto_contrato} onChange={e => setForm({ ...form, monto_contrato: e.target.value })} /></div>
                <div><label className={LABEL}>Moneda</label>
                  <select className={INPUT} value={form.moneda} onChange={e => setForm({ ...form, moneda: e.target.value })}>
                    <option value="PYG">₲ Guaraníes</option><option value="USD">USD</option><option value="BRL">BRL</option>
                  </select>
                </div>
              </div>
              <div><label className={LABEL}>Responsable / jefe de obra</label><input className={INPUT} value={form.responsable || ""} onChange={e => setForm({ ...form, responsable: e.target.value })} /></div>
              <div><label className={LABEL}>Descripción</label><textarea rows={2} className={INPUT} value={form.descripcion || ""} onChange={e => setForm({ ...form, descripcion: e.target.value })} /></div>
              <div><label className={LABEL}>Observaciones</label><textarea rows={2} className={INPUT} value={form.observaciones || ""} onChange={e => setForm({ ...form, observaciones: e.target.value })} /></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setModal(null)} className={BTN_SEC}>Cancelar</button>
              <button onClick={guardar} disabled={saving} className={BTN_PRI}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Detalle del proyecto ────────────────────────────────────────────────────

function ProyectoDetalle({ proyecto, headers, perms, clientes, onEditar, onEliminar, onChanged }) {
  const [tab, setTab] = useState("resumen");
  const [resumen, setResumen] = useState(null);

  const cargarResumen = useCallback(async () => {
    const res = await fetch(`${API}/admin/constructora/proyectos/${proyecto.id}/resumen`, { headers });
    if (res.ok) setResumen(await res.json());
  }, [proyecto.id, proyecto.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { cargarResumen(); }, [cargarResumen]);

  const cambiarEstado = async (estado) => {
    const motivo = ["pausado", "cancelado"].includes(estado) ? window.prompt("Motivo (opcional):") : null;
    if (motivo === null && ["pausado", "cancelado"].includes(estado)) return;
    const res = await fetch(`${API}/admin/constructora/proyectos/${proyecto.id}/estado`, {
      method: "PATCH", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify({ estado, motivo }),
    });
    if (!res.ok) { toast.error(await errMsg(res, "No se pudo cambiar el estado")); return; }
    toast.success("Estado actualizado");
    onChanged();
  };

  const { hasPermission, user } = useContext(AuthContext);
  const isAdm = ["admin", "super_admin", "gerente"].includes(user?.role);
  const can = (p) => isAdm || hasPermission(p);
  const [anticiposInfo, setAnticiposInfo] = useState(null);
  const [facturarSignal, setFacturarSignal] = useState(null);
  useEffect(() => {
    if (!can("constructora_anticipos.ver")) return;
    fetch(`${API}/admin/constructora/proyectos/${proyecto.id}/anticipos`, { headers })
      .then(r => r.ok ? r.json() : null).then(setAnticiposInfo).catch(() => {});
  }, [proyecto.id, tab]); // eslint-disable-line

  const tabsDef = [
    { k: "resumen", l: "Resumen", i: Layers, show: true },
    { k: "presupuesto", l: "Presupuesto", i: ClipboardList, show: can("constructora_presupuesto.ver") },
    { k: "cronograma", l: "Cronograma", i: Calendar, show: can("constructora_cronograma.ver") },
    { k: "compras", l: "Compras", i: ShoppingCart, show: can("constructora_compras.ver") && can("compras.ver") },
    { k: "gastos", l: "Gastos", i: Receipt, show: can("constructora_gastos.ver") && can("costos_fijos.ver") },
    { k: "equipos", l: "Equipos", i: Wrench, show: can("constructora_equipos.ver") },
    { k: "personal", l: "Personal", i: Users, show: can("constructora_personal.ver") },
    { k: "facturacion", l: "Facturación y anticipos", i: DollarSign, show: can("constructora_facturacion.ver") || can("constructora_anticipos.ver") },
    { k: "cobros", l: "Plan de cobros", i: ListChecks, show: true },
    { k: "contrato", l: "Contrato", i: FileText, show: true },
    { k: "reportes", l: "Reportes", i: FileText, show: can("constructora_reportes.ver") },
    { k: "datos", l: "Datos y cliente", i: User, show: true },
  ];
  const irA = (k, signal) => { if (signal) setFacturarSignal({ ...signal, t: Date.now() }); setTab(k); };
  const proximos = tabsDef.filter(t => !["resumen", "cobros", "contrato", "datos"].includes(t.k) && t.show);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <EstadoBadge estado={proyecto.estado} />
          {perms.canEstado && (
            <select className={`${INPUT} w-44`} value="" onChange={e => e.target.value && cambiarEstado(e.target.value)}>
              <option value="">Cambiar estado…</option>
              {Object.entries(ESTADOS).filter(([k]) => k !== proyecto.estado).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          )}
        </div>
        <div className="flex gap-2">
          {perms.canEditar && <button onClick={onEditar} className={BTN_SEC}><Pencil className="w-4 h-4" /> Editar</button>}
          {perms.canEliminar && <button onClick={onEliminar} className={`${BTN_SEC} text-rose-400`}><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>

      <div className="flex gap-1 border-b border-white/10 overflow-x-auto">
        {tabsDef.filter(t => t.show).map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${tab === t.k ? "text-white border-blue-500 bg-blue-500/10" : "text-slate-400 border-transparent hover:text-white hover:border-white/20"}`}>
            <t.i className="w-4 h-4 shrink-0" /> <span className="whitespace-nowrap">{t.l}</span>
          </button>
        ))}
      </div>

      {tab === "resumen" && resumen && (
        <div className="space-y-5">
          <div className="grid gap-4 lg:grid-cols-[auto,1fr]">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col items-center justify-center">
              <Dona pct={resumen.porcentaje_cobrado} />
              <div className="text-xs text-slate-400 mt-1">{resumen.cuotas_cobradas} de {resumen.cuotas_total} cuotas cobradas</div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 content-start">
              {[{ l: "Monto del contrato", v: resumen.monto_contrato, c: "text-white" },
                { l: "Cobrado", v: resumen.cobrado, c: "text-emerald-400" },
                { l: "Pendiente de cobro", v: resumen.pendiente_cobro, c: "text-amber-400" },
                { l: "Vencido sin cobrar", v: resumen.vencido, c: "text-rose-400" }].map(k => (
                <div key={k.l} className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="text-xs text-slate-400">{k.l}</div>
                  <div className={`text-lg font-bold mt-1 ${k.c}`}>{fmtNum(k.v, resumen.moneda)}</div>
                </div>
              ))}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 sm:col-span-2">
                <div className="text-xs text-slate-400">Próxima cuota a cobrar</div>
                {resumen.proxima_cuota ? (
                  <div className="mt-1 text-sm text-white">{resumen.proxima_cuota.concepto} — <span className="text-emerald-400 font-medium">{fmtNum(resumen.proxima_cuota.monto, resumen.moneda)}</span> <span className="text-slate-400">({resumen.proxima_cuota.fecha_prevista || "sin fecha"})</span></div>
                ) : <div className="mt-1 text-sm text-slate-500">Sin cuotas pendientes</div>}
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 sm:col-span-2">
                <div className="text-xs text-slate-400">Plazo</div>
                <div className="mt-1 text-sm text-white">{proyecto.fecha_inicio || "—"} → {proyecto.fecha_fin_prevista || "—"}</div>
                {resumen.dias_atraso_proyecto > 0 && <div className="text-xs text-rose-400 mt-0.5">{resumen.dias_atraso_proyecto} días de atraso sobre el fin previsto</div>}
              </div>
            </div>
          </div>

          {anticiposInfo && (anticiposInfo.total > 0) && (
            <div className="grid gap-3 sm:grid-cols-3">
              {[["Anticipos recibidos", anticiposInfo.total, "text-sky-400"], ["Aplicado a facturas", anticiposInfo.aplicado, "text-white"], ["Anticipo disponible", anticiposInfo.disponible, "text-amber-400"]].map(([l, v, c]) => (
                <div key={l} className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="text-xs text-slate-400">{l}</div>
                  <div className={`text-lg font-bold mt-1 ${c}`}>{fmtNum(v, resumen.moneda)}</div>
                </div>
              ))}
            </div>
          )}

          <div>
            <div className="text-xs text-slate-400 mb-2">Accesos directos (sin salir del proyecto)</div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {proximos.map(s => (
                <button key={s.k} onClick={() => irA(s.k)} className="flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-left transition-colors">
                  <s.i className="w-5 h-5 text-arandu-blue" />
                  <div className="text-sm text-white">{s.l}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "presupuesto" && <PresupuestoTab proyecto={proyecto} />}
      {tab === "cronograma" && <CronogramaTab proyecto={proyecto} />}
      {tab === "compras" && <ComprasGastosTab proyecto={proyecto} tipo="compras" />}
      {tab === "gastos" && <ComprasGastosTab proyecto={proyecto} tipo="costos" />}
      {tab === "equipos" && <EquiposTab proyecto={proyecto} />}
      {tab === "personal" && <PersonalTab proyecto={proyecto} />}
      {tab === "facturacion" && <FacturacionTab proyecto={proyecto} clientes={clientes} openSignal={facturarSignal} />}
      {tab === "reportes" && <ReportesTab proyecto={proyecto} />}
      {tab === "cobros" && (
        <div className="space-y-3">
          {can("constructora_facturacion.facturar") && can("facturas.crear") && (proyecto.plan_cobros || []).some(c => c.estado !== "cobrado") && (
            <button className={BTN_SEC} onClick={() => irA("facturacion", { origen: "cuota" })}><Receipt className="w-4 h-4" /> Facturar una cuota</button>
          )}
          <PlanCobros proyecto={proyecto} headers={headers} canEdit={perms.canPlan} onSaved={() => { cargarResumen(); onChanged(); }} />
        </div>
      )}
      {tab === "contrato" && <Contratos proyecto={proyecto} headers={headers} canVer={perms.canContratoVer} canCargar={perms.canContratoCargar} onChanged={onChanged} />}
      {tab === "datos" && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-1.5 text-sm">
            <div className="text-xs text-slate-400 mb-1">Cliente</div>
            <div className="text-white font-medium">{proyecto.cliente_razon_social || proyecto.cliente_nombre || "Sin cliente"}</div>
            {proyecto.cliente_ruc && <div className="text-slate-300">RUC/CI: {proyecto.cliente_ruc}</div>}
            {proyecto.cliente_telefono && <div className="text-slate-300">Tel: {proyecto.cliente_telefono}</div>}
            {proyecto.cliente_email && <div className="text-slate-300">{proyecto.cliente_email}</div>}
            {proyecto.cliente_direccion && <div className="text-slate-300">{proyecto.cliente_direccion}</div>}
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-1.5 text-sm">
            <div className="text-xs text-slate-400 mb-1">Obra</div>
            {proyecto.tipo_obra && <div className="text-slate-300">Tipo: {proyecto.tipo_obra}</div>}
            {proyecto.ubicacion && <div className="text-slate-300">Ubicación: {proyecto.ubicacion}</div>}
            {proyecto.responsable && <div className="text-slate-300">Responsable: {proyecto.responsable}</div>}
            {proyecto.descripcion && <div className="text-slate-300">{proyecto.descripcion}</div>}
            {proyecto.observaciones && <div className="text-slate-500 text-xs">{proyecto.observaciones}</div>}
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:col-span-2">
            <div className="text-xs text-slate-400 mb-2">Historial de estados</div>
            <div className="space-y-1">
              {[...(proyecto.estado_historial || [])].reverse().map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-slate-300">
                  <EstadoBadge estado={h.estado} />
                  <span>{(h.fecha || "").slice(0, 16).replace("T", " ")}</span>
                  <span className="text-slate-500">{h.usuario}</span>
                  {h.motivo && <span className="text-slate-400">— {h.motivo}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Plan de cobros ──────────────────────────────────────────────────────────

function PlanCobros({ proyecto, headers, canEdit, onSaved }) {
  const [cuotas, setCuotas] = useState(proyecto.plan_cobros || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setCuotas(proyecto.plan_cobros || []); }, [proyecto.id, proyecto.updated_at]); // eslint-disable-line

  const upd = (i, patch) => setCuotas(c => c.map((x, j) => j === i ? { ...x, ...patch } : x));
  const total = cuotas.reduce((a, c) => a + (Number(c.monto) || 0), 0);
  const contrato = Number(proyecto.monto_contrato) || 0;

  const guardar = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/constructora/proyectos/${proyecto.id}/plan-cobros`, {
        method: "PUT", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ cuotas: cuotas.map(c => ({ ...c, monto: Number(c.monto) || 0 })) }),
      });
      if (!res.ok) throw new Error(await errMsg(res, "Error al guardar"));
      toast.success("Plan de cobros guardado");
      onSaved();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="space-y-3">
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-xs text-slate-400 text-left border-b border-white/10">
            <th className="px-3 py-2">Concepto</th><th className="px-3 py-2">Fecha prevista</th><th className="px-3 py-2 text-right">Monto</th><th className="px-3 py-2">Estado</th><th className="px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {cuotas.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">Sin cuotas. Definí cómo te va a pagar el cliente (anticipo, avances, saldo final…).</td></tr>}
            {cuotas.map((c, i) => (
              <tr key={c.id || i} className="border-b border-white/5">
                <td className="px-3 py-2"><input disabled={!canEdit} className={INPUT} value={c.concepto || ""} onChange={e => upd(i, { concepto: e.target.value })} placeholder="Anticipo, Avance 30%…" /></td>
                <td className="px-3 py-2"><DateInput disabled={!canEdit} className={INPUT} value={c.fecha_prevista || ""} onChange={e => upd(i, { fecha_prevista: e.target.value })} /></td>
                <td className="px-3 py-2"><input disabled={!canEdit} type="number" min="0" className={`${INPUT} text-right`} value={c.monto ?? ""} onChange={e => upd(i, { monto: e.target.value })} /></td>
                <td className="px-3 py-2">
                  <select disabled={!canEdit} className={INPUT} value={c.estado || "pendiente"} onChange={e => upd(i, { estado: e.target.value })}>
                    <option value="pendiente">Pendiente</option><option value="cobrado">Cobrado</option>
                  </select>
                </td>
                <td className="px-3 py-2">{canEdit && <button onClick={() => setCuotas(l => l.filter((_, j) => j !== i))} className="text-rose-400 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-slate-300">
          Total del plan: <span className="text-white font-semibold">{fmtNum(total, proyecto.moneda)}</span>
          {contrato > 0 && <span className={`ml-2 text-xs ${Math.round(total) === Math.round(contrato) ? "text-emerald-400" : "text-amber-400"}`}>
            {Math.round(total) === Math.round(contrato) ? "coincide con el contrato" : `diferencia con el contrato: ${fmtNum(contrato - total, proyecto.moneda)}`}
          </span>}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <button onClick={() => setCuotas(l => [...l, { concepto: "", fecha_prevista: "", monto: "", estado: "pendiente" }])} className={BTN_SEC}><Plus className="w-4 h-4" /> Agregar cuota</button>
            <button onClick={guardar} disabled={saving} className={BTN_PRI}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar plan</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Contrato ────────────────────────────────────────────────────────────────

function Contratos({ proyecto, headers, canVer, canCargar, onChanged }) {
  const [subiendo, setSubiendo] = useState(false);

  const subir = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("El archivo supera el máximo de 8 MB"); return; }
    setSubiendo(true);
    try {
      const data = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1]);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const res = await fetch(`${API}/admin/constructora/proyectos/${proyecto.id}/contratos`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ nombre: file.name, filename: file.name, mime: file.type, data }),
      });
      if (!res.ok) throw new Error(await errMsg(res, "Error al subir"));
      toast.success("Archivo cargado");
      onChanged();
    } catch (e) { toast.error(e.message || "Error al subir"); }
    setSubiendo(false);
  };

  const descargar = async (c) => {
    const res = await fetch(`${API}/admin/constructora/proyectos/${proyecto.id}/contratos/${c.id}`, { headers });
    if (!res.ok) { toast.error(await errMsg(res, "No se pudo descargar")); return; }
    const d = await res.json();
    const bin = atob(d.data); const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes], { type: d.mime }));
    const a = document.createElement("a"); a.href = url; a.download = d.filename || "contrato"; a.click();
    URL.revokeObjectURL(url);
  };

  const borrar = async (c) => {
    if (!window.confirm(`¿Eliminar "${c.nombre}"?`)) return;
    const res = await fetch(`${API}/admin/constructora/proyectos/${proyecto.id}/contratos/${c.id}`, { method: "DELETE", headers });
    if (!res.ok) { toast.error(await errMsg(res, "Error al eliminar")); return; }
    onChanged();
  };

  return (
    <div className="space-y-3">
      {canCargar && (
        <label className={`${BTN_PRI} w-fit cursor-pointer`}>
          {subiendo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Cargar archivo (PDF, imagen, Word…)
          <input type="file" className="hidden" onChange={e => { subir(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
      )}
      <div className="bg-white/5 border border-white/10 rounded-xl divide-y divide-white/5">
        {(proyecto.contratos || []).length === 0 && <div className="px-4 py-8 text-center text-slate-500 text-sm">Sin archivos de contrato cargados.</div>}
        {(proyecto.contratos || []).map(c => (
          <div key={c.id} className="flex items-center gap-3 px-4 py-3">
            <FileText className="w-5 h-5 text-slate-400" />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate">{c.nombre}</div>
              <div className="text-[11px] text-slate-500">{(c.fecha || "").slice(0, 10)} · {c.subido_por} · {Math.round((c.tamano || 0) / 1024)} KB</div>
            </div>
            {canVer && <button onClick={() => descargar(c)} className={BTN_SEC}><Download className="w-4 h-4" /></button>}
            {canCargar && <button onClick={() => borrar(c)} className={`${BTN_SEC} text-rose-400`}><Trash2 className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Configuración: unidades de medida ───────────────────────────────────────

function ConfigSecciones({ headers, logoQs, canGrupos, canUnidades, isSuper }) {
  const [seccion, setSeccion] = useState(null);
  const tarjetas = [
    { id: "grupos", icon: Layers, titulo: "Grupos de compra/gasto", desc: "Agrupan las compras y gastos de la obra en los reportes (Vidrios, Albañilería…). Propios de esta empresa." },
    { id: "unidades", icon: ClipboardList, titulo: "Unidades de medida", desc: "Unidades que se ofrecen al cargar ítems y sub-ítems de los presupuestos (m2, m3, kg, jornal…)." },
  ];
  if (seccion) {
    const t = tarjetas.find(x => x.id === seccion);
    return (
      <div className="space-y-4">
        <button onClick={() => setSeccion(null)} className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Configuración · <span className="text-white font-medium">{t?.titulo}</span>
        </button>
        {seccion === "grupos" && <ConfigGrupos headers={headers} logoQs={logoQs} canGrupos={canGrupos} isSuper={isSuper} />}
        {seccion === "unidades" && <ConfigUnidades headers={headers} logoQs={logoQs} canUnidades={canUnidades} />}
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">Configuración del módulo Constructora.</p>
      {tarjetas.map(({ id, icon: Icon, titulo, desc }) => (
        <div key={id} className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl p-4">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-amber-400" /></div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-white">{titulo}</h3>
            <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
          </div>
          <button onClick={() => setSeccion(id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold shrink-0">
            <Settings className="w-3.5 h-3.5" /> Gestionar
          </button>
        </div>
      ))}
    </div>
  );
}

function ConfigUnidades({ headers, logoQs, canUnidades }) {
  const [unidades, setUnidades] = useState([]);
  const [nuevo, setNuevo] = useState("");
  const [nuevaDesc, setNuevaDesc] = useState("");
  const [editId, setEditId] = useState(null);
  const [editNombre, setEditNombre] = useState("");
  const [editDesc, setEditDesc] = useState("");

  const cargar = useCallback(async () => {
    const r = await fetch(`${API}/admin/constructora/unidades${logoQs}`, { headers });
    if (r.ok) setUnidades(await r.json());
  }, [logoQs]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [cargar]);

  const llamar = async (url, method, body, okMsg) => {
    const res = await fetch(url, { method, headers: { ...headers, "Content-Type": "application/json" }, body: body ? JSON.stringify(body) : undefined });
    if (!res.ok) { toast.error(await errMsg(res, "Error")); return false; }
    if (okMsg) toast.success(okMsg);
    await cargar();
    return true;
  };

  const agregar = async () => {
    if (!nuevo.trim()) return;
    if (await llamar(`${API}/admin/constructora/unidades${logoQs}`, "POST", { nombre: nuevo, descripcion: nuevaDesc, activo: true }, "Unidad agregada")) { setNuevo(""); setNuevaDesc(""); }
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-white">Unidades de medida de esta empresa</h3>
        <p className="text-xs text-slate-500">Son las unidades que se ofrecen al cargar ítems y sub-ítems de los presupuestos (m2, m3, kg, jornal…). Son propias de esta empresa. Los presupuestos ya cargados no cambian al editar o borrar una unidad.</p>
      </div>
      {canUnidades && (
        <div className="flex flex-wrap items-center gap-2">
          <input className={`${INPUT} w-32`} maxLength={20} value={nuevo} onChange={e => setNuevo(e.target.value)} onKeyDown={e => e.key === "Enter" && agregar()} placeholder="Unidad (m2)" />
          <input className={`${INPUT} flex-1 min-w-[180px]`} value={nuevaDesc} onChange={e => setNuevaDesc(e.target.value)} onKeyDown={e => e.key === "Enter" && agregar()} placeholder="Descripción (metro cuadrado)" />
          <button onClick={agregar} className={BTN_PRI}><Plus className="w-4 h-4" /> Agregar</button>
        </div>
      )}
      <div className="bg-white/5 border border-white/10 rounded-xl divide-y divide-white/5 max-h-[60vh] overflow-y-auto">
        {unidades.map(u => (
          <div key={u.id} className="flex items-center gap-2 px-3 py-2">
            {editId === u.id ? (
              <>
                <input className={`${INPUT} w-32`} maxLength={20} value={editNombre} onChange={e => setEditNombre(e.target.value)} />
                <input className={`${INPUT} flex-1`} value={editDesc} onChange={e => setEditDesc(e.target.value)} />
                <button className={BTN_PRI} onClick={async () => { if (await llamar(`${API}/admin/constructora/unidades/${u.id}`, "PUT", { nombre: editNombre, descripcion: editDesc, activo: u.activo }, "Unidad actualizada")) setEditId(null); }}><Save className="w-4 h-4" /></button>
                <button className={BTN_SEC} onClick={() => setEditId(null)}><X className="w-4 h-4" /></button>
              </>
            ) : (
              <>
                <span className={`w-24 text-sm font-semibold ${u.activo ? "text-white" : "text-slate-500 line-through"}`}>{u.nombre}</span>
                <span className={`flex-1 text-sm ${u.activo ? "text-slate-300" : "text-slate-600 line-through"}`}>{u.descripcion || "—"}</span>
                {canUnidades && (
                  <>
                    <button title={u.activo ? "Desactivar" : "Activar"} onClick={() => llamar(`${API}/admin/constructora/unidades/${u.id}`, "PUT", { nombre: u.nombre, descripcion: u.descripcion || "", activo: !u.activo })} className="text-slate-400 hover:text-white"><CheckCircle2 className={`w-4 h-4 ${u.activo ? "text-emerald-400" : ""}`} /></button>
                    <button onClick={() => { setEditId(u.id); setEditNombre(u.nombre); setEditDesc(u.descripcion || ""); }} className="text-slate-400 hover:text-white"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => window.confirm(`¿Eliminar la unidad "${u.nombre}" de esta empresa?`) && llamar(`${API}/admin/constructora/unidades/${u.id}`, "DELETE", null, "Unidad eliminada")} className="text-rose-400 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </>
            )}
          </div>
        ))}
        {unidades.length === 0 && <div className="px-4 py-6 text-center text-slate-500 text-sm">Sin unidades.</div>}
      </div>
    </div>
  );
}

// ── Configuración: grupos de compra/gasto ───────────────────────────────────

function ConfigGrupos({ headers, logoQs, canGrupos, isSuper }) {
  const [grupos, setGrupos] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [nuevo, setNuevo] = useState("");
  const [comoDefault, setComoDefault] = useState(false);
  const [nuevoDef, setNuevoDef] = useState("");
  const [editId, setEditId] = useState(null);
  const [editNombre, setEditNombre] = useState("");

  const cargar = useCallback(async () => {
    const r = await fetch(`${API}/admin/constructora/grupos${logoQs}`, { headers });
    if (r.ok) setGrupos(await r.json());
    if (isSuper) {
      const d = await fetch(`${API}/admin/constructora/grupos-default`, { headers });
      if (d.ok) setDefaults(await d.json());
    }
  }, [logoQs, isSuper]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [cargar]);

  const llamar = async (url, method, body, okMsg) => {
    const res = await fetch(url, { method, headers: { ...headers, "Content-Type": "application/json" }, body: body ? JSON.stringify(body) : undefined });
    if (!res.ok) { toast.error(await errMsg(res, "Error")); return false; }
    if (okMsg) toast.success(okMsg);
    await cargar();
    return true;
  };

  const agregar = async () => {
    if (!nuevo.trim()) return;
    if (await llamar(`${API}/admin/constructora/grupos${logoQs}`, "POST", { nombre: nuevo, activo: true, guardar_como_default: isSuper && comoDefault }, "Grupo agregado")) { setNuevo(""); setComoDefault(false); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-white">Grupos de compra/gasto de esta empresa</h3>
          <p className="text-xs text-slate-500">Sirven para agrupar las compras y gastos de la obra en los reportes (Vidrios, Albañilería…). Son propios de esta empresa: editar o borrar acá no afecta a ninguna otra.</p>
        </div>
        {canGrupos && (
          <div className="flex flex-wrap items-center gap-2">
            <input className={`${INPUT} flex-1 min-w-[180px]`} value={nuevo} onChange={e => setNuevo(e.target.value)} onKeyDown={e => e.key === "Enter" && agregar()} placeholder="Nuevo grupo…" />
            {isSuper && <label className="flex items-center gap-1.5 text-xs text-slate-300"><input type="checkbox" checked={comoDefault} onChange={e => setComoDefault(e.target.checked)} /> Guardar como predeterminado del módulo</label>}
            <button onClick={agregar} className={BTN_PRI}><Plus className="w-4 h-4" /> Agregar</button>
          </div>
        )}
        <div className="bg-white/5 border border-white/10 rounded-xl divide-y divide-white/5 max-h-[60vh] overflow-y-auto">
          {grupos.map(g => (
            <div key={g.id} className="flex items-center gap-2 px-3 py-2">
              {editId === g.id ? (
                <>
                  <input className={`${INPUT} flex-1`} value={editNombre} onChange={e => setEditNombre(e.target.value)} />
                  <button className={BTN_PRI} onClick={async () => { if (await llamar(`${API}/admin/constructora/grupos/${g.id}`, "PUT", { nombre: editNombre, activo: g.activo }, "Grupo actualizado")) setEditId(null); }}><Save className="w-4 h-4" /></button>
                  <button className={BTN_SEC} onClick={() => setEditId(null)}><X className="w-4 h-4" /></button>
                </>
              ) : (
                <>
                  <span className={`flex-1 text-sm ${g.activo ? "text-white" : "text-slate-500 line-through"}`}>{g.nombre}</span>
                  {g.origen === "default" && <span className="text-[10px] text-slate-500 border border-white/10 rounded px-1.5">predeterminado</span>}
                  {canGrupos && (
                    <>
                      <button title={g.activo ? "Desactivar" : "Activar"} onClick={() => llamar(`${API}/admin/constructora/grupos/${g.id}`, "PUT", { nombre: g.nombre, activo: !g.activo })} className="text-slate-400 hover:text-white"><CheckCircle2 className={`w-4 h-4 ${g.activo ? "text-emerald-400" : ""}`} /></button>
                      <button onClick={() => { setEditId(g.id); setEditNombre(g.nombre); }} className="text-slate-400 hover:text-white"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => window.confirm(`¿Eliminar el grupo "${g.nombre}" de esta empresa?`) && llamar(`${API}/admin/constructora/grupos/${g.id}`, "DELETE", null, "Grupo eliminado")} className="text-rose-400 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
          {grupos.length === 0 && <div className="px-4 py-6 text-center text-slate-500 text-sm">Sin grupos.</div>}
        </div>
      </div>

      {isSuper && (
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-white">Predeterminados del módulo <span className="text-[10px] text-yellow-400 border border-yellow-400/30 rounded px-1.5 ml-1">solo super admin</span></h3>
            <p className="text-xs text-slate-500">Se copian a cada empresa nueva la primera vez que entra al módulo. Cambiarlos no modifica los grupos que las empresas ya tienen; usá "Importar faltantes" para sumarlos a la empresa activa.</p>
          </div>
          <div className="flex gap-2">
            <input className={`${INPUT} flex-1`} value={nuevoDef} onChange={e => setNuevoDef(e.target.value)} placeholder="Nuevo predeterminado…" />
            <button className={BTN_PRI} onClick={async () => { if (nuevoDef.trim() && await llamar(`${API}/admin/constructora/grupos-default`, "POST", { nombre: nuevoDef })) setNuevoDef(""); }}><Plus className="w-4 h-4" /></button>
            <button className={BTN_SEC} onClick={async () => {
              const r = await fetch(`${API}/admin/constructora/grupos/importar-default${logoQs}`, { method: "POST", headers });
              if (r.ok) { const d = await r.json(); toast.success(`${d.agregados} grupo(s) agregados a la empresa activa`); cargar(); } else toast.error(await errMsg(r, "Error"));
            }}>Importar faltantes</button>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl divide-y divide-white/5 max-h-[60vh] overflow-y-auto">
            {defaults.map(d => (
              <div key={d.id} className="flex items-center gap-2 px-3 py-2">
                <span className="flex-1 text-sm text-white">{d.nombre}</span>
                <button onClick={() => window.confirm(`¿Quitar "${d.nombre}" de los predeterminados?`) && llamar(`${API}/admin/constructora/grupos-default/${d.id}`, "DELETE")} className="text-rose-400 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
